import { EntityState } from '@reduxjs/toolkit';
import { mergeTextIntoPrefab } from './mergeTextIntoPrefab';
import { Prefab } from '@gift/basic-scene-core/Prefab';
import { Json } from '@gift/types/Json';
import { Translate } from '@gift/types/generic/Translate';
import { mergeAssetIntoPrefab } from './mergeAssetIntoPrefab';

export type MergeOptions = {
    direction: 'inputIntoPrefab' | 'prefabIntoInput'
};

export function mergeInputsInto(
    prefab: Prefab,
    userInputs: EntityState<Json.Input<any>>,
    t: Translate,
    options: MergeOptions
) {
    const result: EntityState<Json.Input<any>> = {
        ids: [...prefab.inputs.ids],
        entities: {}
    };

    for (const id of prefab.variables.ids as string[]) {

        const variable = prefab.variables.entities[id]!;
        const prefabInput = localize(prefab.inputs.entities[id]!, t);
        const userInput = userInputs.entities[id];

        result.entities[id] = userInput
            ? combine(userInput, prefabInput, variable, options)
            : prefabInput;

        if (options.direction === 'prefabIntoInput'
            && isEmptyTextInput(userInput, variable.pluginId)) {

            result.entities[id] = clearText(result.entities[id]!);
        }
    }

    return result;
}

function combine(
    userInput: Json.Input<any>,
    prefabInput: Json.Input<any>,
    variable: Json.Variable<any>,
    options: MergeOptions
): Json.Input<any> {
    if (variable.pluginId === 'text') {
        return mergeTextIntoPrefab(prefabInput, userInput, variable.options.maxLength, options.direction);
    } else if (variable.pluginId === 'engraving' || variable.pluginId === 'svg') {
        return mergeAssetIntoPrefab(prefabInput, userInput);
    } else {
        return prefabInput;
    }
}

const isEmptyTextInput = (input: Json.Input<any> | undefined, pluginId: string) => {
    return pluginId === 'text' && (!input || input.value.content === '');
};

const clearText = (input: Json.Input<any>) => {
    return typeof input.value.content === 'string'
        ? edit(input, { content: '' })
        : input;
};

const localize = (input: Json.Input<any>, t: Translate): Json.Input<any> => {
    return typeof input.value.content === 'string'
        ? edit(input, { content: t(input.value.content) })
        : input;
};

export const edit = <T>(
    input: Json.Input<T>,
    changes: Partial<T>
): Json.Input<T> => {
    return {
        ...input,
        value: {
            ...input.value,
            ...changes
        }
    };
};